import { useEffect, useState, useRef } from 'react'
import { DocumentArrowDownIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import 'styles/AgroDataEdit.css'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import AgroDataEditModal from 'components/modals/AgroDataEditModal'
import PageLoadingState from 'components/common/PageLoadingState'
import { useAuth } from 'auth/AuthContext'
import { DateTime } from 'luxon'

export default function AgroDataEdit () {
  const hasFetched = useRef(false)
  const currentFilterDate = useRef(null)
  const { user } = useAuth()
  const [selectedRows, setSelectedRows] = useState([])
  const [state, setState] = useState({
    agroData: [],
    currentDate: parseDate(new Date()),
    filterDate: DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd'),
    searchTerm: '',
    showEditModal: false,
    selectedAgroData: null,
    isBulkEdit: false,
    loading: true
  })

  const getAgroData = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/data-edit/agro-data/${user.farmId}?filterDate=${new Date(state.filterDate).toISOString()}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      setState(s => ({ ...s, agroData: response.data, loading: false }))
    } catch (error) {
      console.error('Error:', error)
      setState(s => ({ ...s, loading: false }))
    }
  }

  const openSingleEditModal = (row) => {
    setState(s => ({ ...s, showEditModal: true, selectedAgroData: row, isBulkEdit: false }))
  }

  const openBulkEditModal = () => {
    setState(s => ({ ...s, showEditModal: true, selectedAgroData: null, isBulkEdit: true }))
  }

  const closeModal = () => {
    setState(s => ({ ...s, showEditModal: false, selectedAgroData: null, isBulkEdit: false }))
  }

  const handleSaveAgroData = async (formData) => {
    try {
      if (state.isBulkEdit) {
        const ids = selectedRows.map(r => r.agroDataId)
        await makeRequest({
          method: 'PATCH',
          url: '/management/data-edit/agro-data/bulk',
          data: { ids, ...formData },
          headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
        })
      } else {
        await makeRequest({
          method: 'PATCH',
          url: `/management/data-edit/agro-data/${state.selectedAgroData.agroDataId}`,
          data: formData,
          headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
        })
      }
      showAlert({ text: 'Agro data updated', icon: 'success' })
      closeModal()
      await getAgroData()
    } catch (error) {
      showAlert({ text: 'Failed to update agro data', icon: 'error' })
      console.error('Error updating agro data:', error)
    }
  }

  const handleDeleteAgroData = async (row) => {
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/management/data-edit/agro-data/${row.agroDataId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      showAlert({ text: 'Agro data deleted', icon: 'success' })
      await getAgroData()
    } catch (error) {
      showAlert({ text: 'Failed to delete agro data', icon: 'error' })
      console.error('Error deleting agro data:', error)
    }
  }

  const filterAgroData = state.agroData.filter(data =>
    (data.rowCode ?? '').toLowerCase().includes(state.searchTerm) ||
    (data.activityName ?? '').toLowerCase().includes(state.searchTerm)
  )

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current && currentFilterDate.current === state.filterDate) return
    hasFetched.current = true
    currentFilterDate.current = state.filterDate
    getAgroData()
  }, [user?.farmId, state.filterDate])

  if (state.loading) {
    return <PageLoadingState message='Loading agro data...' rows={8} />
  }

  return (
    <div className='p-6 bg-white shadow-md border border-gray-300 border-t-0'>
      <div className='section-header'>
        <div className='header-left'>
          <div className='date-filter'>
            <span>Today ({state.currentDate})</span>
          </div>
        </div>

        <div className='flex items-center gap-4 mb-6'>
          {selectedRows.length > 0 && (
            <ButtonComponent className='flex items-center gap-2' onClick={openBulkEditModal}>
              <PencilSquareIcon className='w-5 h-5' />
              <span>Edit selection ({selectedRows.length})</span>
            </ButtonComponent>
          )}
          <ButtonComponent className='flex items-center gap-2'>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as Excel</span>
          </ButtonComponent>
          <ButtonComponent className='flex items-center gap-2'>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as PDF</span>
          </ButtonComponent>
        </div>
      </div>

      <div className='search-container flex gap-4 mb-4'>
        <input
          type='text'
          placeholder='Search'
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='w-48 mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <input
          type='date'
          value={state.filterDate}
          onChange={e => setState(s => ({ ...s, filterDate: e.target.value }))}
          className='w-40 mt-2 mb-2 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
      </div>

      <div className='data-table'>
        {state.agroData.length !== 0 && (
          <TableComponent
            tableData={{
              hasCheckbox: true,
              columns: [
                { key: 'rowCode', label: 'Barcode Number' },
                { key: 'date', label: 'Date', cell: (value) => parseDate(value) },
                { key: 'activityName', label: 'Activity Name' },
                { key: 'shared', label: 'Shared', cell: (value) => value ? 'Yes' : 'No' },
                { key: 'workerName', label: 'Worker' },
                { key: 'blockName', label: 'Block' },
                { key: 'location', label: 'Location' },
                { key: 'completionPercentage', label: '% Complete', cell: (value) => value != null ? `${value}%` : '-' },
                { key: 'actions', label: 'Manage' }
              ],
              rows: filterAgroData
            }}
            handleEdit={openSingleEditModal}
            handleDelete={handleDeleteAgroData}
            onSelectionChange={setSelectedRows}
            defaultSortColumn='rowCode'
          />
        )}
      </div>

      <AgroDataEditModal
        isOpen={state.showEditModal}
        handleClose={closeModal}
        handleSave={handleSaveAgroData}
        selectedAgroData={state.selectedAgroData}
        isBulk={state.isBulkEdit}
        bulkCount={selectedRows.length}
        farmId={user.farmId}
      />
    </div>
  )
}
